import { type JSX } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import clsx from "clsx";
import { motion } from "framer-motion";
import RegistryIcon from "@/shared/assets/svg/icons/abnormal.svg?react";
import { useObjects } from "@entities/object";
import { projectQueries } from "@entities/project";
import { useFlags } from "@shared/lib/flags";
import { routes, useNavigate } from "@shared/lib/react-router";
import "./navigation-menu.scss";
import {
  DocstorePatch,
  JournalsPatch,
  ObjectPatch,
  RequisitesPatch,
  StatisticsPatch,
  SupervisionPatch,
} from "./icon.patches";

const menuItems: {
  link: (projectId: string, objectId: string, titleId?: string) => string;
  iconPatches: JSX.Element;
  label: string;
  isEnabled: (...args: any[]) => boolean;
  name: string;
  isExternal?: boolean;
}[] = [
  {
    link: (projectId) => routes.objects.root(projectId),
    label: "Объекты",
    iconPatches: <ObjectPatch />,
    isEnabled: () => true,
    name: "objects",
  },
  {
    link: (projectId) => routes.project.requisites(projectId),
    label: "Реквизиты",
    iconPatches: <RequisitesPatch />,
    isEnabled: () => true,
    name: "requisites",
  },
  {
    link: (projectId) => routes.project.statistics(projectId),
    label: "Статистика",
    iconPatches: <StatisticsPatch />,
    isEnabled: () => true,
    name: "statistics",
  },
  {
    link: (projectId) => routes.project.docstore(projectId),
    label: "Документарий",
    iconPatches: <DocstorePatch />,
    isEnabled: () => true,
    name: "docstore",
  },
  {
    link: (projectId) => routes.general.recordsRegister(projectId),
    label: "Реестр актов",
    iconPatches: <RegistryIcon />,
    isEnabled: () => true,
    name: "registry",
  },
  {
    link: (projectId, objectId) => routes.objects.journals(projectId, objectId),
    label: "Журналы",
    iconPatches: <JournalsPatch />,
    isEnabled: (isLoading, objectId) => isLoading && objectId,
    name: "journals",
  },
  {
    link: (projectId) => routes.supervision.root(projectId),
    label: "Надзорное дело",
    iconPatches: <SupervisionPatch />,
    isEnabled: () => true,
    name: "supervision",
  },
];

export const NavigationMenu = ({
  collapsed,
  projectId,
  integrationId,
}: {
  collapsed: boolean;
  projectId: string;
  integrationId: string;
}) => {
  const navigate = useNavigate();
  const isActsRegistryEnabled = useFlags("actsRegistryEnabled");
  const { data: objects, isLoading: isObjectsLoading } = useObjects({
    projectId,
  });

  const firstObjectId = objects?.buildingObjects[0]?.buildingObjectId;

  const isShow = (item: any) =>
    !item.isEnabled || (item.name === "journals" && !firstObjectId)
      ? "disabled-navigation-link"
      : "";

  const { data: project = null } = useQuery(
    projectQueries.getProject({
      projectId,
      idType: "projectId",
    }),
  );

  const handleMenuClick = (item: any) => {
    if (item.isExternal) {
      window.open(
        item.link(project?.externalId ?? "", firstObjectId!, integrationId),
        "_blank",
      );
      return;
    }
    if (item.isEnabled(!isObjectsLoading, firstObjectId)) {
      navigate(item.link(projectId, firstObjectId!, integrationId));
    }
  };

  const visibleMenuItems = menuItems.filter(
    (item) => item.name !== "registry" || isActsRegistryEnabled,
  );

  return (
    <div
      className={clsx({
        "left-sider-project-menu": true,
        "left-sider-project-menu-collapsed": collapsed,
      })}
    >
      {visibleMenuItems.map((item) => (
        <Tooltip
          placement="right"
          title={collapsed ? item.label : undefined}
          key={item.label}
        >
          {item.isExternal ? (
            <a
              className={clsx("menu-item-link", isShow(item))}
              href={`${item.link(project?.externalId ?? "", firstObjectId!, integrationId)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.name === "journals" && isObjectsLoading ? (
                <motion.span
                  animate={{ position: "absolute", x: collapsed ? 12 : 0 }}
                >
                  <LoadingOutlined />
                </motion.span>
              ) : (
                <motion.svg
                  animate={{ position: "absolute", x: collapsed ? 12 : 0 }}
                  transition={{ duration: 0.5 }}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {item.iconPatches}
                </motion.svg>
              )}
              <motion.span
                initial={{
                  position: "absolute",
                  marginLeft: "32px",
                  x: collapsed ? 36 : -36,
                }}
                animate={{ opacity: collapsed ? 0 : 1, x: collapsed ? 16 : 0 }}
                transition={{ duration: 0.5 }}
              >
                {item.label}
              </motion.span>
            </a>
          ) : (
            <div
              className={clsx("menu-item-link", isShow(item))}
              onClick={() => handleMenuClick(item)}
            >
              {item.name === "journals" && isObjectsLoading ? (
                <motion.span
                  animate={{ position: "absolute", x: collapsed ? 12 : 0 }}
                >
                  <LoadingOutlined />
                </motion.span>
              ) : (
                <motion.svg
                  animate={{ position: "absolute", x: collapsed ? 12 : 0 }}
                  transition={{ duration: 0.5 }}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {item.iconPatches}
                </motion.svg>
              )}
              <motion.span
                initial={{
                  position: "absolute",
                  marginLeft: "32px",
                  x: collapsed ? 36 : -36,
                }}
                animate={{ opacity: collapsed ? 0 : 1, x: collapsed ? 16 : 0 }}
                transition={{ duration: 0.5 }}
              >
                {item.label}
              </motion.span>
            </div>
          )}
        </Tooltip>
      ))}
    </div>
  );
};
